import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { validateConfig } from "@hsl/core/fund-explorer/validators";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import { type Config } from "./config/types";
import { useBannerImage } from "./hooks/useBannerImage";
import { OpenAPI } from "./openapi";
import { ExplorerStoreProvider } from "./store/store";
import FundExplorerView from "./views/FundExplorerView";

import "../tailwind.css";

OpenAPI.BASE = `${window.location.origin}/srp/api/fcs`;
// OpenAPI.BASE = "https://p3-fundcentres-lgim.huguenots.local/srp/api/fcs";

persistVersion();

interface Props {
    config: Config;
}

const FundExplorer = ({ config }: Props) => {
    const isValidConfig = validateConfig(config);
    const setLatestFC = useFCSubscriptionStore((store) => store.setLatestFC);
    const { pathname } = useLocation();

    useEffect(() => {
        const importantInfo = document.querySelectorAll(
            "#important-information > div",
        );
        importantInfo.forEach((info) => {
            if (info.classList.contains(config.productType)) {
                info.classList.remove("hidden");
            } else {
                info.classList.add("hidden");
            }
        });
        return () => {
            const importantInfo = document.querySelectorAll(
                "#important-information > div",
            );
            importantInfo.forEach((info) => {
                info.classList.add("hidden");
            });
        };
    }, [config.productType]);

    useEffect(() => {
        setLatestFC(pathname as "/" | "/pmc" | "/etf");
    }, [setLatestFC, pathname]);

    // fade between banners
    // works by setting an absolutely positioned ::after element with the
    // old banner as a background image, then fading it out
    useBannerImage(config);

    return isValidConfig ? (
        <ExplorerStoreProvider config={config}>
            <FundExplorerView />
        </ExplorerStoreProvider>
    ) : (
        <div>Invalid fund explorer config</div>
    );
};

export default FundExplorer;

function persistVersion() {
    const searchParams = new URLSearchParams(window.location.search);
    const version = searchParams.get("version");
    const branch = searchParams.get("branch");
    const noCache = searchParams.has("no-cache");

    const linkSearchParams = new URLSearchParams();
    if (version) {
        linkSearchParams.set("version", version);
    }
    if (branch) {
        linkSearchParams.set("branch", branch);
    }
    if (noCache) {
        linkSearchParams.set("no-cache", "true");
    }
    const links =
        document.querySelectorAll<HTMLAnchorElement>("a.fund-centre-tab");
    links.forEach((link) => {
        const existing = new URL(link.href);
        existing.search = linkSearchParams.toString();
        link.href = existing.toString();
    });
}
